import { EXPENSE_DOC_KIND_LABELS, EXPENSE_PAYMENT_METHOD_LABELS, type Expense } from "@station/contracts";
import { Alert, Button, Dialog, FormField, SkeletonRows, StatusBadge, Textarea, useToast } from "@station/ui";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Download, Pencil, Send, X } from "lucide-react";
import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import { Amount } from "../../../../features/accounting/shared.js";
import { ApprovalRequestPanel } from "../../../../features/approvals/request-panel.js";
import { useExpense, useRefreshExpenses } from "../../../../features/expenses/queries.js";
import {
  ExpenseLedgerBadge,
  ExpensesPage,
  ExpenseStatusBadge,
  Field,
  Section,
  useExpensePermissions,
  useExpensesCrumb,
} from "../../../../features/expenses/shared.js";
import { api } from "../../../../lib/api.js";
import { errorMessage } from "../../../../lib/errors.js";
import { formatDate, formatDateTime } from "../../../../lib/format.js";
import { queryKeys } from "../../../../lib/query.js";
import { useStepUp } from "../../../../lib/step-up.js";

export const Route = createFileRoute("/_app/giderler/$id/")({ component: ExpenseDetailPage });

function ExpenseDetailPage(): ReactElement {
  const { t } = useTranslation();
  const { id } = Route.useParams();
  const crumb = useExpensesCrumb("/giderler");
  const expense = useExpense(id);
  const title = expense.data?.expenseNo ?? t("expenses.detail.title");

  return (
    <ExpensesPage title={title} breadcrumb={[crumb, { label: title }]}>
      {() =>
        expense.error ? (
          <Alert tone="danger">{errorMessage(expense.error)}</Alert>
        ) : !expense.data ? (
          <SkeletonRows rows={8} />
        ) : (
          <ExpenseDetail expense={expense.data} />
        )
      }
    </ExpensesPage>
  );
}

function ExpenseDetail({ expense }: { expense: Expense }): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const withStepUp = useStepUp();
  const refresh = useRefreshExpenses();
  const permissions = useExpensePermissions();
  const [cancelling, setCancelling] = useState(false);
  const [reason, setReason] = useState("");

  const act = useMutation({
    mutationFn: async (action: "submit" | "cancel") => {
      const params = { path: { id: expense.id } };
      if (action === "submit")
        return api.POST("/expenses/{id}/submit", { params, body: { version: expense.version } });
      // İptal hassas bir işlemdir: gerekirse yeniden kimlik doğrulama penceresi açılır ve işlem yinelenir.
      return withStepUp(() =>
        api.POST("/expenses/{id}/cancel", { params, body: { reason, version: expense.version } }),
      );
    },
    onSuccess: async (_data, action) => {
      setCancelling(false);
      setReason("");
      await refresh(expense.companyId);
      toast({ tone: "success", title: t(`expenses.detail.done.${action}`) });
    },
    onError: (error) => toast({ tone: "danger", title: errorMessage(error) }),
  });

  return (
    <div className="grid gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-lg font-semibold">
          {expense.expenseNo ?? t("expenses.list.draftNo")}
        </span>
        <ExpenseStatusBadge status={expense.status} />
        <ExpenseLedgerBadge status={expense.ledgerStatus} />
        <StatusBadge tone="neutral">{EXPENSE_DOC_KIND_LABELS[expense.docKind]}</StatusBadge>
        <div className="ms-auto flex flex-wrap gap-2">
          {expense.status === "DRAFT" && permissions.canManage ? (
            <>
              <Button variant="outline" asChild>
                <Link to="/giderler/$id/duzenle" params={{ id: expense.id }}>
                  <Pencil aria-hidden="true" />
                  {t("common.edit")}
                </Link>
              </Button>
              <Button loading={act.isPending} onClick={() => act.mutate("submit")}>
                <Send aria-hidden="true" />
                {t("expenses.form.submit")}
              </Button>
            </>
          ) : null}
          {(expense.status === "APPROVED" || expense.status === "PENDING_APPROVAL") &&
          permissions.canCancel ? (
            <Button variant="destructive" onClick={() => setCancelling(true)}>
              <X aria-hidden="true" />
              {t("expenses.detail.cancel")}
            </Button>
          ) : null}
        </div>
      </div>

      {expense.rejectedReason ? (
        <Alert tone="danger" title={t("expenses.detail.rejectedTitle")}>
          {expense.rejectedReason}
        </Alert>
      ) : null}
      {expense.cancelReason ? (
        <Alert tone="warning" title={t("expenses.detail.cancelledTitle")}>
          {expense.cancelReason}
        </Alert>
      ) : null}

      <Section title={t("expenses.detail.summary")}>
        <dl className="grid gap-x-6 gap-y-3 text-sm sm:grid-cols-2 lg:grid-cols-4">
          <Field label={t("expenses.fields.category")}>{expense.categoryName}</Field>
          <Field label={t("expenses.fields.docNo")}>{expense.docNo ?? "—"}</Field>
          <Field label={t("expenses.fields.docDate")}>{formatDate(expense.docDate)}</Field>
          <Field label={t("expenses.fields.dueDate")}>{formatDate(expense.dueDate)}</Field>
          <Field label={t("expenses.fields.paymentMethod")}>
            {EXPENSE_PAYMENT_METHOD_LABELS[expense.paymentMethod]}
          </Field>
          <Field label={t("expenses.fields.netAmount")}>
            <Amount value={expense.netAmount} />
          </Field>
          <Field label={t("expenses.fields.vatAmount")}>
            <Amount value={expense.vatAmount} /> (%{expense.vatRate})
          </Field>
          <Field label={t("expenses.fields.withholdingAmount")}>
            <Amount value={expense.withholdingAmount} />
          </Field>
          <Field label={t("expenses.fields.grossAmount")}>
            <Amount value={expense.grossAmount} />
          </Field>
          <Field label={t("expenses.fields.payableAmount")}>
            <Amount value={expense.payableAmount} />
          </Field>
          <Field label={t("expenses.fields.submittedAt")}>
            {expense.submittedAt ? formatDateTime(expense.submittedAt) : "—"}
          </Field>
          <Field label={t("expenses.fields.approvedAt")}>
            {expense.approvedAt ? formatDateTime(expense.approvedAt) : "—"}
          </Field>
        </dl>
        <p className="text-sm">{expense.description}</p>
      </Section>

      {expense.allocations.length > 0 ? (
        <Section title={t("expenses.detail.allocations")}>
          {/* Paylar ONAY anında dondurulur: anahtar sonradan değişse de kesilmiş fiş değişmez. */}
          <p className="text-xs text-muted-foreground">{t("expenses.detail.allocationsFrozen")}</p>
          <div className="overflow-x-auto rounded-lg border">
            <table className="w-full text-sm">
              <caption className="sr-only">{t("expenses.detail.allocations")}</caption>
              <thead className="bg-muted/60 text-xs uppercase text-muted-foreground">
                <tr>
                  <th scope="col" className="px-3 py-2 text-left">
                    {t("expenses.fields.station")}
                  </th>
                  <th scope="col" className="px-3 py-2 text-left">
                    {t("expenses.fields.businessUnit")}
                  </th>
                  <th scope="col" className="px-3 py-2 text-right">
                    {t("expenses.fields.amount")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {expense.allocations.map((share, index) => (
                  <tr key={`${share.stationId ?? "none"}-${index}`} className="border-t">
                    <td className="px-3 py-2">{share.stationName ?? t("expenses.fields.wholeCompany")}</td>
                    <td className="px-3 py-2">
                      {share.businessUnitType ? t(`enums.businessUnit.${share.businessUnitType}`) : "—"}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Amount value={share.amount} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      ) : null}

      {expense.expenseNo ? <JournalEntries companyId={expense.companyId} expenseId={expense.id} /> : null}

      {expense.fileId ? <DocumentFile fileId={expense.fileId} /> : null}

      {expense.approvalRequestId ? (
        <Section title={t("expenses.detail.approval")}>
          <ApprovalRequestPanel requestId={expense.approvalRequestId} showSummary={false} />
        </Section>
      ) : null}

      <Dialog
        open={cancelling}
        onOpenChange={setCancelling}
        title={t("expenses.detail.cancelTitle")}
        description={t("expenses.detail.cancelDescription")}
      >
        <form
          className="grid gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            act.mutate("cancel");
          }}
        >
          <FormField label={t("expenses.detail.cancelReason")} required>
            {(control) => (
              <Textarea
                {...control}
                required
                minLength={3}
                maxLength={500}
                value={reason}
                onChange={(event) => setReason(event.target.value)}
              />
            )}
          </FormField>
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button type="button" variant="outline" onClick={() => setCancelling(false)}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" variant="destructive" loading={act.isPending}>
              {t("expenses.detail.cancel")}
            </Button>
          </div>
        </form>
      </Dialog>
    </div>
  );
}

/**
 * Gideri muhasebeleştiren fiş(ler) — kaynak BELGEYE göre süzülür (eskiden gider numarasıyla metin araması
 * yapılırdı). Ters kayıt ayrı bir kaynak taşır (`JOURNAL_ENTRY_REVERSAL`) ve burada görünmez; asıl fişin
 * durumu "Ters kayıtlandı" olarak okunur.
 */
function JournalEntries({
  companyId,
  expenseId,
}: {
  companyId: string;
  expenseId: string;
}): ReactElement | null {
  const { t } = useTranslation();
  const query = { companyId, sourceDocType: "EXPENSE", sourceDocId: expenseId, limit: 10 };
  const entries = useQuery({
    queryKey: queryKeys.accounting(companyId, "entries", query),
    queryFn: async () => (await api.GET("/accounting/journal-entries", { params: { query } })).data!.items,
  });
  if (!entries.data || entries.data.length === 0) return null;
  return (
    <Section title={t("expenses.detail.journalEntries")}>
      <ul className="grid gap-1 text-sm">
        {entries.data.map((entry) => (
          <li key={entry.id}>
            <Link
              to="/muhasebe/fisler/$id"
              params={{ id: entry.id }}
              className="underline underline-offset-4"
            >
              {entry.entryNo ?? t("expenses.detail.draftEntry")} · {formatDate(entry.postingDate)} ·{" "}
              {entry.description}
            </Link>
          </li>
        ))}
      </ul>
    </Section>
  );
}

/** Belge görseli: imzalı indirme adresi yalnızca istendiğinde alınır (adres kısa ömürlüdür). */
function DocumentFile({ fileId }: { fileId: string }): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const download = useMutation({
    mutationFn: async () =>
      (await api.GET("/files/{id}/download-link", { params: { path: { id: fileId } } })).data!,
    onSuccess: (data) => window.open(data.url, "_blank", "noopener"),
    onError: (error) => toast({ tone: "danger", title: errorMessage(error) }),
  });
  return (
    <Section title={t("expenses.detail.documentFile")}>
      <Button
        variant="outline"
        className="justify-self-start"
        loading={download.isPending}
        onClick={() => download.mutate()}
      >
        <Download aria-hidden="true" />
        {t("expenses.detail.openDocumentFile")}
      </Button>
    </Section>
  );
}
